import React, { useEffect, useState } from 'react';

/**
 * Viewport-width-based drop-in replacements for react-device-detect's
 * `BrowserView` / `MobileView`.
 *
 * react-device-detect decides desktop-vs-mobile from the User-Agent, which is
 * unreliable on tablets (a modern iPad reports a desktop UA and gets the
 * desktop layout). On the donation pages we want tablets to use the same
 * compact "Donate Now -> modal" layout as phones, so we switch on the actual
 * viewport width instead.
 *
 * The render output matches react-device-detect exactly (a wrapping <div> that
 * receives any passed props such as `style`/`className`, or a fragment when
 * `renderWithFragment` is set), so these can be swapped in by changing only the
 * import line.
 *
 * <= COMPACT_MAX_WIDTH  -> MobileView renders (phones + tablets, incl. iPad
 *                          landscape at 1194px)
 * >  COMPACT_MAX_WIDTH  -> BrowserView renders (desktop)
 */
export const COMPACT_MAX_WIDTH = 1194;

const getIsCompact = (): boolean =>
  typeof window !== 'undefined' ? window.innerWidth <= COMPACT_MAX_WIDTH : false;

const useIsCompact = (): boolean => {
  const [isCompact, setIsCompact] = useState<boolean>(getIsCompact());

  useEffect(() => {
    const onResize = () => setIsCompact(getIsCompact());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return isCompact;
};

interface ViewProps {
  children?: React.ReactNode;
  renderWithFragment?: boolean;
  [key: string]: any;
}

export const BrowserView: React.FC<ViewProps> = ({
  children,
  renderWithFragment,
  ...props
}) => {
  const isCompact = useIsCompact();
  if (isCompact) return null;
  return renderWithFragment ? (
    <>{children}</>
  ) : (
    <div {...props}>{children}</div>
  );
};

export const MobileView: React.FC<ViewProps> = ({
  children,
  renderWithFragment,
  ...props
}) => {
  const isCompact = useIsCompact();
  if (!isCompact) return null;
  return renderWithFragment ? (
    <>{children}</>
  ) : (
    <div {...props}>{children}</div>
  );
};
